/**
 * features/onboarding/useOnboarding — Wizard state + provisioning hook.
 *
 * Responsibilities:
 *   - Own the 3-step draft (account/business/branch) with LocalStorage sync
 *   - Expose `setStep`, `updateAccount`, `updateBusiness`, `updateBranch`
 *   - Validate a step (Zod) on demand — for enable/disable of "Next"
 *   - Provide `provision()` that posts to the mock API and, on success,
 *     seeds the auth store + TenantContext memberships
 *
 * Authority: ADR-108 §6 FE State Management.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { setStoredAuthToken } from '@/shared/api/apiClient';
import { useAuth } from '@/shared/hooks/useAuth';
import {
  clearOnboardingDraft,
  draftToSubmission,
  loadOnboardingDraft,
  saveOnboardingDraft,
} from './draft-storage';
import { OnboardingApiError, provisionTenant } from './onboarding-api';
import { validateStep } from './schemas';
import type {
  AccountStepDraft,
  BranchStepDraft,
  BusinessStepDraft,
  OnboardingDraft,
  OnboardingStep,
  ProvisionTenantResponse,
} from './types';

export interface UseOnboardingApi {
  step: OnboardingStep;
  draft: OnboardingDraft;
  canAdvance: boolean;
  stepErrors: { path: string; message: string }[];
  isProvisioning: boolean;
  provisioningError: OnboardingApiError | null;
  setStep: (step: OnboardingStep) => void;
  nextStep: () => void;
  previousStep: () => void;
  updateAccount: (patch: Partial<Omit<AccountStepDraft, 'password'>>) => void;
  updateBusiness: (patch: Partial<BusinessStepDraft>) => void;
  updateBranch: (patch: Partial<BranchStepDraft>) => void;
  resetDraft: () => void;
  provision: (password: string) => Promise<ProvisionTenantResponse>;
}

function branchPatchMerged(
  current: BranchStepDraft,
  patch: Partial<BranchStepDraft>,
): BranchStepDraft {
  return {
    ...current,
    ...patch,
    address: { ...current.address, ...(patch.address ?? {}) },
  };
}

export function useOnboarding(): UseOnboardingApi {
  const [draft, setDraft] = useState<OnboardingDraft>(() => loadOnboardingDraft());
  const [step, setStepState] = useState<OnboardingStep>(1);
  const [stepErrors, setStepErrors] = useState<{ path: string; message: string }[]>([]);
  const idempotencyKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const { account, business, branch } = draft;
    saveOnboardingDraft({ account, business, branch });
  }, [draft]);

  const validateCurrent = useCallback(
    (candidateStep: OnboardingStep, candidateDraft: OnboardingDraft) => {
      const slice =
        candidateStep === 1
          ? { ...candidateDraft.account, password: 'DraftPassword1' }
          : candidateStep === 2
            ? candidateDraft.business
            : candidateDraft.branch;
      return validateStep(candidateStep, slice);
    },
    [],
  );

  const canAdvance = useMemo(() => {
    const result = validateCurrent(step, draft);
    return result.ok;
  }, [draft, step, validateCurrent]);

  const setStep = useCallback((next: OnboardingStep) => {
    setStepState(next);
    setStepErrors([]);
  }, []);

  const nextStep = useCallback(() => {
    const result = validateCurrent(step, draft);
    if (!result.ok) {
      setStepErrors(result.errors);
      return;
    }
    setStepErrors([]);
    setStepState(prev => (prev === 3 ? 3 : ((prev + 1) as OnboardingStep)));
  }, [draft, step, validateCurrent]);

  const previousStep = useCallback(() => {
    setStepState(prev => (prev === 1 ? 1 : ((prev - 1) as OnboardingStep)));
    setStepErrors([]);
  }, []);

  const updateAccount = useCallback(
    (patch: Partial<Omit<AccountStepDraft, 'password'>>) => {
      setDraft(prev => ({ ...prev, account: { ...prev.account, ...patch } }));
    },
    [],
  );
  const updateBusiness = useCallback((patch: Partial<BusinessStepDraft>) => {
    setDraft(prev => ({ ...prev, business: { ...prev.business, ...patch } }));
  }, []);
  const updateBranch = useCallback((patch: Partial<BranchStepDraft>) => {
    setDraft(prev => ({ ...prev, branch: branchPatchMerged(prev.branch, patch) }));
  }, []);

  const resetDraft = useCallback(() => {
    clearOnboardingDraft();
    setDraft(loadOnboardingDraft());
    setStepState(1);
    setStepErrors([]);
  }, []);

  const mutation = useMutation<ProvisionTenantResponse, OnboardingApiError, string>({
    mutationFn: async (password: string) => {
      if (!idempotencyKeyRef.current) {
        idempotencyKeyRef.current = crypto?.randomUUID?.() ?? `key-${Date.now()}`;
      }
      const submission = draftToSubmission(draft, password);
      return provisionTenant(submission, { idempotencyKey: idempotencyKeyRef.current });
    },
    onSuccess: (res) => {
      setStoredAuthToken(res.auth.token);
      useAuth.setState({ token: res.auth.token, user: res.user, isResolving: false });
      clearOnboardingDraft();
      idempotencyKeyRef.current = null;
    },
  });

  const provision = useCallback(
    (password: string) => mutation.mutateAsync(password),
    [mutation],
  );

  return {
    step,
    draft,
    canAdvance,
    stepErrors,
    isProvisioning: mutation.isPending,
    provisioningError: mutation.error ?? null,
    setStep,
    nextStep,
    previousStep,
    updateAccount,
    updateBusiness,
    updateBranch,
    resetDraft,
    provision,
  };
}
